import moment from "moment-timezone";
import { REPEAT_OPTIONS, newThreadsPart } from "./constants";

/**
 * Utility functions for Post component
 */

export const formatScheduleDate = (selectedDate, selectedTime, timezone) => {
  if (!selectedDate || !selectedTime)
    return { scheduleDate: null, scheduleUtcDate: null };

  const tz = timezone || moment.tz.guess();
  const dateStr = selectedDate.format("YYYY-MM-DD");
  const timeStr = selectedTime.format("HH:mm:ss");

  const zoned = moment
    .tz(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm:ss", tz)
    .second(0)
    .millisecond(0);

  return {
    scheduleDate: zoned.format("YYYY-MM-DD HH:mm:ss"),
    scheduleUtcDate: zoned.clone().utc().format("YYYY-MM-DD HH:mm:ss"),
  };
};

export const formatTags = (tags) => {
  if (!Array.isArray(tags)) return "";
  return tags.map((tag) => `#${tag}`).join(" ");
};

export const formatTagsArray = (tags) => {
  if (!Array.isArray(tags)) return "";
  return tags.map((tag) => (tag.startsWith("#") ? tag : `#${tag}`)).join(" ");
};

export const getMinToDate = (fromDate, repeatOption) => {
  if (!fromDate) return null;

  switch (repeatOption) {
    case REPEAT_OPTIONS.EVERY_WEEK:
      return fromDate.clone().add(1, "week");
    case REPEAT_OPTIONS.EVERY_MONTH:
      return fromDate.clone().add(1, "month");
    case REPEAT_OPTIONS.EVERY_YEAR:
      return fromDate.clone().add(1, "year");
    default:
      return null;
  }
};

export const isTrialEnded = (metaData) => {
  return (metaData?.user?.package ?? metaData?.package) === 8;
};

export const toggleArrayItem = (array, item) => {
  const isSelected = array.includes(item);
  return isSelected ? array.filter((id) => id !== item) : [...array, item];
};

/**
 * Loads draft data from post object and formats it for the form
 * @param {Object} postToEdit - Post object to edit
 * @returns {Object|null} - Formatted data for form state or null if no post
 */
export const loadDraftData = (postToEdit) => {
  if (!postToEdit) return null;

  const safeParse = (v) => {
    if (typeof v !== "string") return v;
    try {
      return JSON.parse(v);
    } catch {
      return v;
    }
  };

  const type_data = safeParse(postToEdit.type_data) || {};
  const tags = safeParse(postToEdit.tags);
  const {
    schedule_date,
    accounts,
    repeat_freq,
    repeat_end,
    draft_collection_id,
    status,
  } = postToEdit;

  // Parse schedule dates
  let selectedDate = null;
  let selectedTime = null;
  let fromDate = null;
  let toDate = null;

  if (schedule_date) {
    const scheduleMoment = moment(schedule_date);
    selectedDate = scheduleMoment;
    selectedTime = scheduleMoment;
    fromDate = scheduleMoment;
  }

  if (repeat_end) {
    toDate = moment(repeat_end);
  }

  // Extract tags from text if they exist
  let extractedTags = Array.isArray(tags) ? tags : [];

  if (type_data?.text && !extractedTags.length) {
    const hashtagMatches = type_data.text.match(/#\w+/g);
    if (hashtagMatches) {
      extractedTags = hashtagMatches.map((tag) => tag.replace("#", ""));
    }
  }

  // Ensure extractedTags is always an array
  if (!Array.isArray(extractedTags)) {
    extractedTags = [];
  }

  // Clean text from hashtags if they were extracted to tags
  let cleanText = type_data?.text || "";
  if (extractedTags.length) {
    const hashtagPattern = /#\w+/g;
    cleanText = cleanText.replace(hashtagPattern, "").trim();
  }

  return {
    postID: postToEdit.id,
    selectedFiles: Array.isArray(type_data?.files) ? type_data.files : [],
    selectedAccounts: Array.isArray(accounts)
      ? accounts.map((acc) => (acc && acc.id ? acc.id : null)).filter(Boolean)
      : [],
    typeContent:
      type_data?.files &&
      Array.isArray(type_data.files) &&
      type_data.files.length > 0
        ? "media"
        : "text",
    tags: extractedTags,
    selectedTime,
    selectedDate,
    fromDate,
    toDate,
    repeatOption: repeat_freq || "no_repeat",
    postTitle: type_data?.title || "",
    postText: cleanText,
    isEditing: true,
    isDraftBeingEdited: status === "draft",
    draftCollectionId: draft_collection_id || null,
    youtubePrivacy: type_data?.privacy || "public",
    youtubeCategoryId: type_data?.category_id || "22",
    // Parts 2..N of a Threads chain, in publish order, with fresh client-only keys. Hashtags are read from
    // part 1 only (above), as they are only ever appended there.
    threadsReplies: Array.isArray(type_data?.threads?.replies)
      ? type_data.threads.replies
          .filter((r) => r && typeof r === "object")
          .map((r) => newThreadsPart(r))
      : [],
  };
};
